import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Alert,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  InsertDriveFile as FileIcon,
  TextFields as TextIcon,
  QuestionAnswer as QuestionIcon,
  Title as TitleIcon,
  FormatQuote as CitationIcon,
  Link as LinkIcon,
  Notifications as NotificationIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import MultilingualInput from './MultilingualInput';
import MarkdownEditorMultilingual from './MarkdownEditorMultilingual';
import FileUpload from './FileUpload';
import ImageUpload from './ImageUpload';
import { sortAdminLanguages } from '../utils/languageOrder';

export interface IModule {
  type: 'title' | 'richtext' | 'video' | 'audio' | 'file' | 'question' | 'citation' | 'link' | 'notification' | 'image';
  order: number;
  content: {
    title?: { [key: string]: string };
    text?: { [key: string]: string };
    fileUrl?: { [key: string]: string };
    fileName?: { [key: string]: string };
    fileType?: { [key: string]: string };
    question?: { [key: string]: string };
    questionType?: 'text' | 'multiple_choice' | 'essay';
    options?: { [key: string]: string }[];
    // Citation
    content?: { [key: string]: string };
    name?: { [key: string]: string };
    description?: { [key: string]: string };
    image?: string;
    avatar?: string;
    // Link
    url?: { [key: string]: string };
    // Notification
    notificationType?: 'takeaway' | 'attention' | 'info' | 'best-practice';
    // Image module (multilingue)
    imageByLang?: { [key: string]: string };
  };
}

export interface ICapsule {
  order: number;
  title: { [key: string]: string };
  description: { [key: string]: string };
  image?: string;
  modules: IModule[];
  estimatedDuration?: number;
  duration?: number; // Alias de estimatedDuration pour compatibilité
  isActive: boolean;
}

interface CapsuleEditorDialogProps {
  open: boolean;
  capsule: ICapsule | null;
  onSave: (capsule: ICapsule) => void;
  onCancel: () => void;
  availableLanguages: string[];
}

const MODULE_TYPES = [
  { value: 'title', label: 'Titre', icon: <TitleIcon />, description: 'Titre de section' },
  { value: 'richtext', label: 'Texte enrichi', icon: <TextIcon />, description: 'Contenu avec mise en forme' },
  { value: 'video', label: 'Vidéo', icon: <VideoIcon />, description: 'Lien vers une vidéo' },
  { value: 'audio', label: 'Audio', icon: <AudioIcon />, description: 'Lien vers un fichier audio' },
  { value: 'file', label: 'Fichier', icon: <FileIcon />, description: 'Document à télécharger' },
  { value: 'question', label: 'Question', icon: <QuestionIcon />, description: 'Question interactive' },
  { value: 'citation', label: 'Citation', icon: <CitationIcon />, description: 'Citation avec auteur' },
  { value: 'link', label: 'Lien', icon: <LinkIcon />, description: 'Lien hypertexte' },
  { value: 'image', label: 'Image', icon: <ImageIcon />, description: 'Image illustrative' },
  { value: 'notification', label: 'Box highlight', icon: <NotificationIcon />, description: 'Encart d\'information' }
];

const CapsuleEditorDialog: React.FC<CapsuleEditorDialogProps> = ({
  open,
  capsule,
  onSave,
  onCancel,
  availableLanguages
}) => {
  const sortedLanguages = React.useMemo(
    () => sortAdminLanguages(availableLanguages),
    [availableLanguages]
  );
  const [editedCapsule, setEditedCapsule] = useState<ICapsule>({
    order: 1,
    title: {},
    description: {},
    modules: [],
    estimatedDuration: 0,
    isActive: true
  });

  const [fileModuleLanguageTab, setFileModuleLanguageTab] = useState(0);
  const fileModuleLangUserSelectedRef = React.useRef(false);

  const [insertModuleMenuIndex, setInsertModuleMenuIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'modules'>('info');

  useEffect(() => {
    if (fileModuleLanguageTab >= sortedLanguages.length) {
      setFileModuleLanguageTab(0);
    }
  }, [fileModuleLanguageTab, sortedLanguages.length]);

  // Keep default file language tab aligned with FR-first until user picks one.
  useEffect(() => {
    if (fileModuleLangUserSelectedRef.current) return;
    const nextDefaultIndex = 0;
    if (fileModuleLanguageTab !== nextDefaultIndex) {
      setFileModuleLanguageTab(nextDefaultIndex);
    }
  }, [sortedLanguages, fileModuleLanguageTab]);

  useEffect(() => {
    if (open) {
      setActiveTab('info');
    }
  }, [open]);

  useEffect(() => {
    if (capsule) {
      console.log('🔍 Capsule reçue:', capsule);
      
      // S'assurer que les objets multilingues ont des valeurs par défaut
      const defaultTitle = availableLanguages.reduce((acc, lang) => {
        acc[lang] = capsule.title[lang] || '';
        return acc;
      }, {} as { [key: string]: string });
      
      const defaultDescription = availableLanguages.reduce((acc, lang) => {
        acc[lang] = capsule.description[lang] || '';
        return acc;
      }, {} as { [key: string]: string });

      // Corriger les modules existants pour s'assurer qu'ils ont la structure multilingue
      const correctedModules = capsule.modules.map(module => {
        const correctedModule = { ...module };
        
        // S'assurer que le content existe et a la bonne structure
        if (!correctedModule.content || typeof correctedModule.content !== 'object') {
          correctedModule.content = {};
        }
        
        // S'assurer que tous les champs de content existent
        const contentFields: (keyof typeof correctedModule.content)[] = ['title', 'text', 'fileUrl', 'fileName', 'fileType', 'question', 'url', 'imageByLang'];
        contentFields.forEach(field => {
          if (!correctedModule.content[field]) {
            (correctedModule.content as any)[field] = availableLanguages.reduce((acc, lang) => {
              acc[lang] = '';
              return acc;
            }, {} as { [key: string]: string });
          } else {
            // S'assurer que toutes les langues sont présentes
            const fieldValue = (correctedModule.content as any)[field];
            if (fieldValue && typeof fieldValue === 'object') {
              availableLanguages.forEach(lang => {
                if (fieldValue[lang] === undefined) {
                  fieldValue[lang] = '';
                }
              });
            }
          }
        });
        
        // Pour les modules file et link, s'assurer que title existe
        if ((correctedModule.type === 'file' || correctedModule.type === 'link') && !correctedModule.content.title) {
          (correctedModule.content as any).title = availableLanguages.reduce((acc, lang) => {
            acc[lang] = '';
            return acc;
          }, {} as { [key: string]: string });
        }
        
        // S'assurer que options existe
        if (!correctedModule.content.options) {
          correctedModule.content.options = [];
        }
        
        return correctedModule;
      });

      const correctedCapsule = {
        ...capsule,
        title: defaultTitle,
        description: defaultDescription,
        modules: correctedModules
      };

      console.log('🔧 Capsule corrigée:', correctedCapsule);
      setEditedCapsule(correctedCapsule);
    } else {
      // Créer des objets avec des valeurs par défaut pour toutes les langues
      const defaultMultilingual = availableLanguages.reduce((acc, lang) => {
        acc[lang] = '';
        return acc;
      }, {} as { [key: string]: string });

      setEditedCapsule({
        order: 1,
        title: defaultMultilingual,
        description: defaultMultilingual,
        modules: [],
        estimatedDuration: undefined, // New capsule, no duration set yet
        isActive: true
      });
    }
  }, [capsule, open, availableLanguages]);

  const handleSave = () => {
    console.log('💾 Sauvegarde de la capsule (Dialog):', editedCapsule);
    console.log('📋 Modules à sauvegarder:', editedCapsule.modules);
    console.log('🔢 Nombre de modules:', editedCapsule.modules.length);
    onSave(editedCapsule);
  };

  const handleCancel = () => {
    onCancel();
  };

  const insertModuleAt = (type: string, insertIndex: number) => {
    // Créer des objets avec des valeurs par défaut pour toutes les langues
    const defaultMultilingual = availableLanguages.reduce((acc, lang) => {
      acc[lang] = '';
      return acc;
    }, {} as { [key: string]: string });

    const newModule: IModule = {
      type: type as any,
      order: insertIndex + 1,
      content: {
        title: { ...defaultMultilingual },
        text: { ...defaultMultilingual },
        fileUrl: { ...defaultMultilingual },
        question: { ...defaultMultilingual },
        options: [],
        // Citation
        content: { ...defaultMultilingual },
        name: { ...defaultMultilingual },
        description: { ...defaultMultilingual },
        // Link
        url: { ...defaultMultilingual },
        // Notification
        notificationType: 'takeaway',
        // Image module
        imageByLang: { ...defaultMultilingual }
      }
    };

    setEditedCapsule(prev => {
      const nextModules = [...prev.modules];
      const safeIndex = Math.max(0, Math.min(insertIndex, nextModules.length));
      nextModules.splice(safeIndex, 0, newModule);
      // Re-index to keep "order" consistent with the list order
      nextModules.forEach((m, idx) => {
        m.order = idx + 1;
      });
      return { ...prev, modules: nextModules };
    });

    setInsertModuleMenuIndex(null);
  };

  const updateModule = (moduleIndex: number, updates: Partial<IModule>) => {
    console.log('🔄 Mise à jour du module:', moduleIndex, updates);
    
    setEditedCapsule(prev => {
      const updatedModules = prev.modules.map((module, index) => {
        if (index === moduleIndex) {
          // Fusionner les mises à jour avec le module existant
          const updatedModule = { ...module, ...updates };
          
          // Si on met à jour le content, fusionner avec le content existant
          if (updates.content) {
            updatedModule.content = { ...module.content, ...updates.content };
          }
          
          console.log('✅ Module mis à jour:', updatedModule);
          return updatedModule;
        }
        return module;
      });

      const updatedCapsule = {
        ...prev,
        modules: updatedModules
      };
      
      console.log('📦 Capsule mise à jour:', updatedCapsule);
      return updatedCapsule;
    });
  };

  const deleteModule = (moduleIndex: number) => {
    const module = editedCapsule.modules[moduleIndex];
    const moduleType = module.type;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce module ${moduleType} ? Cette action est irréversible.`)) {
      setEditedCapsule(prev => ({
        ...prev,
        modules: prev.modules.filter((_, index) => index !== moduleIndex)
      }));
    }
  };

  const moveModule = (moduleIndex: number, direction: 'up' | 'down') => {
    const modules = [...editedCapsule.modules];
    const index = moduleIndex;
    
    if (direction === 'up' && index > 0) {
      [modules[index - 1], modules[index]] = [modules[index], modules[index - 1]];
    } else if (direction === 'down' && index < modules.length - 1) {
      [modules[index], modules[index + 1]] = [modules[index + 1], modules[index]];
    }

    // Mettre à jour les ordres
    modules.forEach((module, idx) => {
      module.order = idx + 1;
    });

    setEditedCapsule(prev => ({
      ...prev,
      modules
    }));
  };

  const renderModuleContent = (module: IModule, moduleIndex: number) => {
    switch (module.type) {
      case 'title':
        return (
          <MultilingualInput
            label="Titre"
            value={module.content.title || {}}
            onChange={(value) => updateModule(moduleIndex, { 
              content: { ...module.content, title: value } 
            })}
            availableLanguages={availableLanguages}
            required
          />
        );
      
      case 'richtext':
        return (
          <MarkdownEditorMultilingual
            label="Contenu (RichText / HTML)"
            value={module.content.text || {}}
            onChange={(value) => updateModule(moduleIndex, { 
              content: { ...module.content, text: value } 
            })}
            availableLanguages={availableLanguages}
            placeholder="Saisissez votre contenu (HTML)..."
            height={400}
          />
        );
      
      case 'video':
      case 'audio':
        return (
          <MultilingualInput
            label="URL"
            value={module.content.fileUrl || {}}
            onChange={(value) => updateModule(moduleIndex, { 
              content: { ...module.content, fileUrl: value } 
            })}
            availableLanguages={availableLanguages}
            required
          />
        );
      
      case 'file':
        // Get the first file URL for the current language (single file only)

        return (
          <Box>
            {/* Title field (multilingual) */}
            <Box sx={{ mb: 3 }}>
              <MultilingualInput
                label="Titre du fichier"
                value={module.content.title || {}}
                onChange={(value) => updateModule(moduleIndex, { 
                  content: { ...module.content, title: value } 
                })}
                availableLanguages={availableLanguages}
              />
            </Box>

            <Tabs
              value={fileModuleLanguageTab}
              onChange={(_, newValue) => {
                fileModuleLangUserSelectedRef.current = true;
                setFileModuleLanguageTab(newValue);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {sortedLanguages.map((language) => (
                <Tab
                  key={language}
                  label={language.toUpperCase()}
                />
              ))}
            </Tabs>
            
            {sortedLanguages.map((language, index) => {
              if (index !== fileModuleLanguageTab) return null;
              
              const languageFileUrl = module.content.fileUrl?.[language] || '';
              const languageHasFile = languageFileUrl && languageFileUrl.trim() !== '';
              const languageUploadedFiles = languageHasFile ? [{
                id: `existing-${language}`,
                originalName: languageFileUrl.split('/').pop() || 'Fichier',
                filename: languageFileUrl.split('/').pop() || 'Fichier',
                url: languageFileUrl.trim(),
                size: 0,
                mimetype: 'application/octet-stream',
                uploadedAt: new Date().toISOString()
              }] : [];

              return (
                <Box key={language} sx={{ mt: 2 }}>
                  <FileUpload
                    onFileUploaded={(file) => {
                      // Replace the existing file (single file only)
                      const newFileUrls = { ...module.content.fileUrl };
                      newFileUrls[language] = file.url;
                      updateModule(moduleIndex, { 
                        content: { 
                          ...module.content, 
                          fileUrl: newFileUrls 
                        } 
                      });
                    }}
                    onFileRemoved={(fileId) => {
                      // Remove the file for this language
                      const newFileUrls = { ...module.content.fileUrl };
                      newFileUrls[language] = '';
                      updateModule(moduleIndex, { 
                        content: { 
                          ...module.content, 
                          fileUrl: newFileUrls 
                        } 
                      });
                    }}
                    uploadedFiles={languageUploadedFiles}
                    maxFiles={1}
                    acceptedTypes={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'csv', 'mp3', 'wav', 'mp4', 'webm', 'avi', 'mov']}
                    multiple={false}
                    compact
                  />
                </Box>
              );
            })}
          </Box>
        );

      case 'image':
        return (
          <Box sx={{ mt: 1 }}>
            <Tabs
              value={fileModuleLanguageTab}
              onChange={(_, newValue) => {
                fileModuleLangUserSelectedRef.current = true;
                setFileModuleLanguageTab(newValue);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1 }}
            >
              {sortedLanguages.map((language) => (
                <Tab
                  key={language}
                  label={language.toUpperCase()}
                />
              ))}
            </Tabs>

            {sortedLanguages.map((language, index) => {
              if (index !== fileModuleLanguageTab) return null;

              const currentImage = module.content.imageByLang?.[language] || '';

              return (
                <Box key={language} sx={{ mt: 1 }}>
                  <ImageUpload
                    label={`Image (${language.toUpperCase()})`}
                    currentImage={currentImage}
                    onImageUploaded={(imageUrl) => {
                      const next = { ...(module.content.imageByLang || {}) };
                      next[language] = imageUrl;
                      updateModule(moduleIndex, {
                        content: {
                          ...module.content,
                          imageByLang: next
                        }
                      });
                    }}
                    onImageRemoved={() => {
                      const next = { ...(module.content.imageByLang || {}) };
                      next[language] = '';
                      updateModule(moduleIndex, {
                        content: {
                          ...module.content,
                          imageByLang: next
                        }
                      });
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        );
      
      case 'question':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MultilingualInput
              label="Question (suivi admin uniquement)"
              value={module.content.question || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, question: value } 
              })}
              availableLanguages={availableLanguages}
              required
            />
            <MultilingualInput
              label="Placeholder du champ text"
              value={module.content.title || {}}
              onChange={(value) => updateModule(moduleIndex, {
                content: { ...module.content, title: value }
              })}
              availableLanguages={availableLanguages}
            />
          </Box>
        );
      
      case 'citation':
        return (
          <Box>
            <MarkdownEditorMultilingual
              label="Contenu de la citation"
              value={module.content.content || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, content: value } 
              })}
              availableLanguages={availableLanguages}
              placeholder="Saisissez la citation..."
              height={200}
            />
            <MultilingualInput
              label="Nom de l'auteur"
              value={module.content.name || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, name: value } 
              })}
              availableLanguages={availableLanguages}
              required
            />
            <MultilingualInput
              label="Description / Titre de l'auteur"
              value={module.content.description || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, description: value } 
              })}
              availableLanguages={availableLanguages}
            />
            <Box sx={{ mt: 2 }}>
              <ImageUpload
                label="Photo de l'auteur"
                currentImage={module.content.image || module.content.avatar}
                onImageUploaded={(imageUrl) => updateModule(moduleIndex, { 
                  content: { ...module.content, image: imageUrl, avatar: imageUrl } 
                })}
                onImageRemoved={() => updateModule(moduleIndex, { 
                  content: { ...module.content, image: undefined, avatar: undefined } 
                })}
                forcedAspectRatio={1}
                hideAspectRatioSelector
              />
            </Box>
          </Box>
        );
      
      case 'link':
        return (
          <Box>
            {/* Title field (multilingual) */}
            <Box sx={{ mb: 3 }}>
              <MultilingualInput
                label="Titre du lien"
                value={module.content.title || {}}
                onChange={(value) => updateModule(moduleIndex, { 
                  content: { ...module.content, title: value } 
                })}
                availableLanguages={availableLanguages}
              />
            </Box>
            <MultilingualInput
              label="URL du lien"
              value={module.content.url || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, url: value } 
              })}
              availableLanguages={availableLanguages}
              required
            />
          </Box>
        );
      
      case 'notification':
        return (
          <Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Type de Box highlight</InputLabel>
              <Select
                value={module.content.notificationType || 'takeaway'}
                label="Type de Box highlight"
                onChange={(e) => updateModule(moduleIndex, { 
                  content: { ...module.content, notificationType: e.target.value } 
                })}
              >
                <MenuItem value="takeaway">À emporter - Jaune</MenuItem>
                <MenuItem value="attention">Attention - Rouge</MenuItem>
                <MenuItem value="info">Info - Bleu</MenuItem>
                <MenuItem value="best-practice">Bonne pratique - Vert</MenuItem>
              </Select>
            </FormControl>
            <MarkdownEditorMultilingual
              label="Contenu de la Box highlight"
              value={module.content.content || {}}
              onChange={(value) => updateModule(moduleIndex, { 
                content: { ...module.content, content: value } 
              })}
              availableLanguages={availableLanguages}
              placeholder="Saisissez le contenu de la Box highlight..."
              height={250}
            />
          </Box>
        );
      
      default:
        return <Typography>Type de module non supporté</Typography>;
    }
  };

  const renderInsertModulePlus = (insertIndex: number) => {
    const isOpen = insertModuleMenuIndex === insertIndex;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 1 }}>
        <Tooltip title="Insérer un module ici">
          <IconButton
            size="small"
            onClick={() => setInsertModuleMenuIndex(insertIndex)}
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'grey.50',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {isOpen && (
          <Box
            sx={{
              mt: 1,
              width: '100%',
              maxWidth: 520,
              p: 1.5,
              borderRadius: 2,
              border: '1px dashed #e0e0e0',
              bgcolor: 'grey.50'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Choisir le type à insérer
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {MODULE_TYPES.map((type) => (
                <Button
                  key={type.value}
                  variant="outlined"
                  size="small"
                  startIcon={type.icon}
                  onClick={() => insertModuleAt(type.value, insertIndex)}
                >
                  {type.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleCancel();
      }}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        {capsule ? 'Modifier la capsule' : 'Nouvelle capsule'}
      </DialogTitle>
      
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Informations" value="info" />
          <Tab label="Modules" value="modules" />
        </Tabs>

        {activeTab === 'info' && (
          <>
            <Box sx={{ mb: 3 }}>
              <MultilingualInput
                label="Titre de la capsule"
                value={editedCapsule.title}
                onChange={(value) => setEditedCapsule(prev => ({ ...prev, title: value }))}
                availableLanguages={availableLanguages}
                required
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <MultilingualInput
                label="Label"
                value={editedCapsule.description}
                onChange={(value) => setEditedCapsule(prev => ({ ...prev, description: value }))}
                availableLanguages={availableLanguages}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <ImageUpload
                label="Image de la capsule"
                currentImage={editedCapsule.image}
                onImageUploaded={(imageUrl) => setEditedCapsule(prev => ({ ...prev, image: imageUrl }))}
                onImageRemoved={() => setEditedCapsule(prev => ({ ...prev, image: undefined }))}
                forcedAspectRatio={9 / 3}
                hideAspectRatioSelector
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Durée estimée (minutes)"
                type="number"
                value={editedCapsule.estimatedDuration ?? ''}
                onChange={(e) => setEditedCapsule(prev => ({ 
                  ...prev, 
                  estimatedDuration: e.target.value ? parseInt(e.target.value) : undefined 
                }))}
                fullWidth
                margin="normal"
              />
            </Box>
          </>
        )}

        {activeTab === 'modules' && (
          <>
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Modules ({editedCapsule.modules.length})
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              {renderInsertModulePlus(0)}
            </Box>

            {editedCapsule.modules.map((module, index) => {
              const moduleType = MODULE_TYPES.find(t => t.value === module.type);
              const stableModuleKey = (module as any)._id || `module-${module.order}-${index}`;

              return (
                <React.Fragment key={stableModuleKey}>
                  <Card
                    sx={{
                      mb: 2,
                      border: '2px solid',
                      borderColor: 'grey.700',
                      borderRadius: 2,
                      boxShadow: 'none'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {moduleType?.icon}
                        <Typography variant="subtitle1" sx={{ ml: 1, flexGrow: 1 }}>
                          {moduleType?.label} #{module.order}
                        </Typography>

                        <Tooltip title="Monter">
                          <IconButton
                            size="small"
                            onClick={() => moveModule(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUpIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Descendre">
                          <IconButton
                            size="small"
                            onClick={() => moveModule(index, 'down')}
                            disabled={index === editedCapsule.modules.length - 1}
                          >
                            <ArrowDownIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => {
                              console.log('Modification du module:', module);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => deleteModule(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {renderModuleContent(module, index)}
                    </CardContent>
                  </Card>

                  {renderInsertModulePlus(index + 1)}
                </React.Fragment>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} startIcon={<CancelIcon />}>
          Annuler
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={<SaveIcon />}
          disabled={!editedCapsule.title[availableLanguages[0]]}
        >
          Sauvegarder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CapsuleEditorDialog;
